/**
 * analytics.js — Check a Payment commercial funnel
 * ---------------------------------------------------------------
 * PRODUCT.md §9 / FEATURES.md "Analytics": sensitive customer
 * evidence, case content, form content, uploaded documents and
 * verdict detail must NEVER reach marketing/session-recording
 * analytics. `track()` enforces this at the call site by only
 * accepting a fixed event name plus a small, explicit allow-list of
 * coarse properties — there is no free-form payload, so a future
 * caller cannot accidentally pass evidence through this function.
 *
 * No analytics provider is configured in this environment
 * (`ANALYTICS_WRITE_KEY` is documented but unset in `.env.example`).
 * `track()` currently only logs to the console in development, so
 * the funnel is instrumented end to end but wired to nothing. Wiring
 * a real provider is a separate, explicit decision — swap the body
 * of `send()` for a real call once one is approved.
 */

export const FUNNEL_EVENTS = Object.freeze({
  VISITOR: "pc_visitor",
  CTA_CLICK: "pc_cta_click",
  PRICING_VIEWED: "pc_pricing_viewed",
  STRIPE_STARTED: "pc_stripe_started",
  PAYMENT_COMPLETED: "pc_payment_completed",
  INTAKE_STARTED: "pc_intake_started",
  INTAKE_COMPLETED: "pc_intake_completed",
  INVESTIGATION_COMPLETED: "pc_investigation_completed",
  REVIEW_ENQUIRY: "pc_review_enquiry",
  REFERRAL_ENQUIRY: "pc_referral_enquiry",
  // Added for the "next iteration" brief — finer-grained funnel steps,
  // same "pc_" naming convention as the events above. Additive only:
  // nothing above was removed or renamed.
  PRODUCT_SELECTED: "pc_product_selected",
  CUSTOMER_DETAILS_STARTED: "pc_customer_details_started",
  CUSTOMER_DETAILS_COMPLETED: "pc_customer_details_completed",
  TERMS_ACCEPTED: "pc_terms_accepted",
  DEMO_PAYMENT_VIEWED: "pc_demo_payment_viewed",
  DEMO_PAYMENT_COMPLETED: "pc_demo_payment_completed",
  RECOVERY_ENQUIRY_STARTED: "pc_recovery_enquiry_started",
  RECOVERY_ENQUIRY_COMPLETED: "pc_recovery_enquiry_completed",
  REFERRAL_PARTNER_OPENED: "pc_referral_partner_opened",
  REFERRAL_PARTNER_SUBMITTED: "pc_referral_partner_submitted",
});

// Only these coarse, non-identifying, non-content properties may ever
// accompany an event. Anything else is dropped before it "leaves"
// this module — see `sanitize()`. `channel` is the coarse acquisition
// channel (FEATURES.md "Analytics"); `surface` is which on-page
// element triggered the event (hero/pricing/carousel/etc) — kept
// separate so the two "channel" concepts never collide.
const ALLOWED_PROPERTIES = new Set([
  "language",
  "channel",
  "surface",
  "device",
  "product",
  "abandonment_point",
]);

/** Coarse device bucket — no fingerprinting, just a viewport check. */
export function coarseDevice() {
  if (typeof window === "undefined") return "unknown";
  return window.innerWidth < 768 ? "mobile" : "desktop";
}

/** Coarse acquisition channel — direct vs. referral, nothing more specific. */
export function coarseAcquisitionChannel() {
  if (typeof document === "undefined" || !document.referrer) return "direct";
  try {
    const referrerHost = new URL(document.referrer).host;
    return referrerHost && referrerHost !== window.location.host ? "referral" : "direct";
  } catch {
    return "direct";
  }
}

function sanitize(properties) {
  if (!properties) return {};
  const clean = {};
  for (const key of Object.keys(properties)) {
    if (ALLOWED_PROPERTIES.has(key)) {
      clean[key] = properties[key];
    }
  }
  return clean;
}

function send(event, properties) {
  // No analytics provider is wired up (see file header) — this is
  // the entire "send" path today. Logging to the console keeps the
  // funnel visible during implementation/QA without pretending data
  // is reaching anything in production. This is a static browser
  // bundle with no Node `process` global available at runtime, so
  // this deliberately does not attempt a dev/production branch here.
  // eslint-disable-next-line no-console
  console.info("[pc-analytics]", event, properties);
}

export function track(eventName, properties) {
  if (!Object.values(FUNNEL_EVENTS).includes(eventName)) {
    // eslint-disable-next-line no-console
    console.warn("[pc-analytics] unknown event", eventName);
    return;
  }
  send(eventName, sanitize(properties));
}
